import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { Button, Checkbox, DialogContentText, FormControlLabel, useTheme } from "@mui/material";
import { Dialog } from "components/Dialog";
import { FACILITY_INFORMATION_SECTION_ID } from "encamp-shared/src/hmbp/sections";
import { ActivityStatus, ActivityType } from "generated-graphql/graphql";
import { useUpdateActivity } from "hooks/activities";
import { useCreateTierIIReportComment } from "hooks/comments";
import { useActivityLogs } from "hooks/useActivityLogs";
import { useState } from "react";

import { ActivityButtonProps } from ".";
import { useReportDetails } from "../../useReportDetails";
import { SectionSelector } from "../SectionSelector";

export const Requeue = ({
  activity,
  showWarningPanel = false,
  context = "report",
  refetchQueries,
}: ActivityButtonProps & {
  showWarningPanel?: boolean;
  context?: "report" | "cohort";
  refetchQueries?: string[];
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [sectionIds, setSectionIds] = useState<string[] | undefined>(undefined);

  const { data } = useReportDetails();
  const [updateActivity, { loading }] = useUpdateActivity(refetchQueries);
  const [createComment] = useCreateTierIIReportComment();

  // Get error message from activity logs
  const { data: logData } = useActivityLogs(activity.id, `status:${ActivityStatus.Error}`, 0, 1, [
    { field: "time", sort: "desc" },
  ]);

  const errorMessage = logData?.activityLogs.items?.[0]?.message ?? null;

  const workflowId = data?.tierIIReport.currentWorkflow?.id;
  const reportId = data?.tierIIReport.id;
  const facilityState = data?.tierIIReport.facility.state;

  // Section selection only applies to report-context Geppetto requeues for CA
  // (CERS HMBP). Cohort-context requeues never carry section IDs.
  const isCaGeppetto =
    context === "report" && activity.type === ActivityType.Geppetto && facilityState === "CA";

  const contextMessage = context === "cohort" ? "cohort activities page" : "report details page";

  const requeueAndUpdateCache = async (sectionIdsForRequeue?: string[]) => {
    await updateActivity({
      variables: {
        id: activity.id,
        input: {
          status: ActivityStatus.Queued,
          message: `Requeued activity from ${contextMessage}.`,
          sectionIds: sectionIdsForRequeue,
        },
      },
      update(cache, { data }) {
        if (data?.updateActivity.status) {
          cache.modify({
            id: cache.identify({
              __typename: "Activity",
              id: activity.id,
            }),
            fields: {
              status() {
                return data?.updateActivity?.status;
              },
            },
          });
        }
      },
    });

    // Add workflow comment with error message
    if (workflowId && reportId && errorMessage) {
      const commentText = `Requeued:\n"${errorMessage}"`;
      await createComment({
        variables: {
          input: {
            reportId,
            workflowId,
            comment: commentText,
          },
        },
      });
    }
  };

  const onClick = () => {
    if (showWarningPanel || isCaGeppetto) {
      setIsDialogOpen(true);
    } else {
      requeueAndUpdateCache();
    }
  };

  const handleDialogSubmit = async () => {
    try {
      await requeueAndUpdateCache(sectionIds);
      setSectionIds(undefined);
      setIsDialogOpen(false);
    } catch {
      // useUpdateActivity surfaces errors via its own state; leave the dialog
      // open so the user can retry without losing their selection.
    }
  };

  const handleDialogCancel = () => {
    setSectionIds(undefined);
    setIsDialogOpen(false);
  };

  return (
    <>
      <Button
        variant="text"
        size="small"
        color="primary"
        loadingPosition="start"
        onClick={onClick}
        startIcon={<PlayArrowIcon />}
        sx={{ minWidth: 100 }}
      >
        Requeue
      </Button>
      {isDialogOpen && (
        <RequeueDialog
          activityId={activity.id}
          isOpen={isDialogOpen}
          onCancel={handleDialogCancel}
          loading={loading}
          context={context}
          showWarningPanel={showWarningPanel}
          showSectionSelector={isCaGeppetto}
          sectionIds={sectionIds}
          onSectionIdsChange={setSectionIds}
          onSubmit={handleDialogSubmit}
        />
      )}
    </>
  );
};

interface Props {
  activityId: string;
  onSubmit: () => void;
  onCancel: () => void;
  isOpen: boolean;
  loading: boolean;
  context: "report" | "cohort";
  showWarningPanel: boolean;
  showSectionSelector: boolean;
  sectionIds: string[] | undefined;
  onSectionIdsChange: (next: string[] | undefined) => void;
}

const RequeueDialog = ({
  isOpen,
  onCancel,
  onSubmit,
  loading,
  context,
  showWarningPanel,
  showSectionSelector,
  sectionIds,
  onSectionIdsChange,
}: Props) => {
  const theme = useTheme();
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [customizeSections, setCustomizeSections] = useState(false);

  const handleRequeueButtonClick = () => {
    onSubmit();
  };

  const handleClose = () => {
    setIsConfirmed(false);
    setCustomizeSections(false);
    onCancel();
  };

  const handleCustomizeToggle = (checked: boolean) => {
    setCustomizeSections(checked);
    if (!checked) {
      onSectionIdsChange(undefined);
    } else {
      // Seed with FI so parent state matches the visible UI (FI is force-checked).
      onSectionIdsChange([FACILITY_INFORMATION_SECTION_ID]);
    }
  };

  const subheaderText = showWarningPanel
    ? context === "cohort"
      ? "Making changes now may affect the cohort workflow, which has already started. Some steps cannot be recalled."
      : "Making changes now may affect the report submission, which has already started. Some steps cannot be recalled, such as submitting a payment."
    : undefined;

  const detailsLinkText = context === "cohort" ? "cohort details" : "report details";

  const requeueDisabled = showWarningPanel ? !isConfirmed : false;

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      title="Requeue Activity"
      primaryActionButtonProps={{
        onClick: handleRequeueButtonClick,
        loading,
        disabled: requeueDisabled,
        saveText: "Requeue",
        startIcon: null,
      }}
      subheader={subheaderText}
      description={
        showWarningPanel ? (
          <DialogContentText>
            Submit a ticket to{" "}
            <a
              href="https://encamp.slack.com/archives/C02EUTUBC1H"
              style={{ textDecoration: "none" }}
            >
              #engineering-support
            </a>{" "}
            for manual review by an engineer with these{" "}
            <a href={window.location.href} style={{ textDecoration: "none" }}>
              {detailsLinkText}
            </a>
            .
          </DialogContentText>
        ) : undefined
      }
    >
      {showWarningPanel && (
        <FormControlLabel
          control={
            <Checkbox
              checked={isConfirmed}
              onChange={(e) => setIsConfirmed(e.target.checked)}
              name="confirmed"
              color="primary"
            />
          }
          label="I've reviewed this with an engineer and want to override the safety check"
        />
      )}
      {showSectionSelector && (
        <>
          <FormControlLabel
            control={
              <Checkbox
                checked={customizeSections}
                onChange={(e) => handleCustomizeToggle(e.target.checked)}
                name="customizeSections"
                color="primary"
              />
            }
            label="Submit only specific CERS sections"
            sx={{ mt: theme.spacing(1) }}
          />
          {customizeSections && (
            <SectionSelector value={sectionIds ?? []} onChange={onSectionIdsChange} />
          )}
        </>
      )}
    </Dialog>
  );
};
